import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { CheckCircle, Loader2 } from 'lucide-react';
import { SCALE_BANDS } from './ProviderContactForm';
import ConfidentialityUnlock from './ConfidentialityUnlock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CAPABILITIES = [
  'Resident-controlled context and evidence sharing',
  'Structured issue / concern intake',
  'Resident correspondence and evidence continuity',
  'Provider response workflows',
  'Notices and resident communications',
  'Portable resident record / history',
  'Structured reporting or exports',
  'Selected integration points',
  'Not sure yet',
];

const TRI_OPTIONS = ['Yes', 'No', 'Unsure'];

const TriChoice = ({ label, value, onChange, testId }) => (
  <div className="space-y-2">
    <Label className="text-[#3B3535] font-medium">{label}</Label>
    <div className="flex gap-2">
      {TRI_OPTIONS.map((opt) => (
        <button
          key={opt}
          type="button"
          data-testid={`${testId}-${opt.toLowerCase()}`}
          onClick={() => onChange(opt)}
          className={`flex-1 py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
            value === opt
              ? 'border-[#B5541B] bg-[#B5541B]/5 text-[#B5541B]'
              : 'border-gray-200 text-[#3B3535] hover:border-[#B5541B]/40'
          }`}
        >
          {opt}
        </button>
      ))}
    </div>
  </div>
);

const EnterpriseContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    role: '',
    scale_band: '',
    existing_systems: '',
    capability: '',
    integration: '',
    sso: '',
    white_label: '',
    target_area: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');

  const set = (key) => (value) => setFormData((f) => ({ ...f, [key]: value }));

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.organisation || !formData.scale_band) {
      setError('Please fill in all required fields.');
      return;
    }
    setIsSubmitting(true);
    setError('');

    const detailParts = [
      formData.role && `Role: ${formData.role}`,
      formData.capability && `Capability of interest: ${formData.capability}`,
      formData.integration && `Integration needed: ${formData.integration}`,
      formData.sso && `SSO / identity requirement: ${formData.sso}`,
      formData.white_label && `White-label interest: ${formData.white_label}`,
      formData.target_area && `Target team / service / portfolio: ${formData.target_area}`,
    ].filter(Boolean);

    const maxRetries = 2;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(`${BACKEND_URL}/api/waiting-list`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            user_type: 'housing_provider',
            organisation: formData.organisation,
            operator_type: formData.role ? `Enterprise — ${formData.role}` : 'Enterprise',
            scale_band: formData.scale_band,
            existing_systems: formData.existing_systems,
            interested_in_pilot: true,
            pain_points: formData.message,
            desired_features: detailParts.join('\n'),
            subscribe_blog: false,
            interest_type: 'provider_enterprise',
            source_page: '/providers/enterprise',
          }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.detail || 'Failed to submit');
        }

        setIsSuccess(true);
        setIsSubmitting(false);
        return;
      } catch (err) {
        if (attempt < maxRetries && err.message === 'Failed to fetch') {
          await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
          continue;
        }
        setError(err.message || 'Something went wrong. Please try again.');
        setIsSubmitting(false);
        return;
      }
    }
  };

  if (isSuccess) {
    return (
      <div data-testid="enterprise-form-success" className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100 text-center space-y-4">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto">
          <CheckCircle className="w-7 h-7 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-[#1F1E1E]">Thanks — we&apos;ll be in touch</h3>
        <p className="text-[#3B3535]/70 max-w-md mx-auto">
          We&apos;ve received your details and will come back to you to arrange a discovery
          conversation about your systems, use case and the right scope to test.
        </p>
        <ConfidentialityUnlock email={formData.email} name={formData.name} source="enterprise-form" />
      </div>
    );
  }

  return (
    <div data-testid="enterprise-contact-form" className="bg-white rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-100">
      <div className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ent-name" className="text-[#3B3535] font-medium">
              Name <span className="text-[#B5541B]">*</span>
            </Label>
            <Input id="ent-name" data-testid="enterprise-name-input" value={formData.name}
              onChange={(e) => set('name')(e.target.value)} placeholder="Your name"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ent-email" className="text-[#3B3535] font-medium">
              Work email <span className="text-[#B5541B]">*</span>
            </Label>
            <Input id="ent-email" data-testid="enterprise-email-input" type="email" value={formData.email}
              onChange={(e) => set('email')(e.target.value)} placeholder="you@organisation.com"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="ent-org" className="text-[#3B3535] font-medium">
              Organisation <span className="text-[#B5541B]">*</span>
            </Label>
            <Input id="ent-org" data-testid="enterprise-org-input" value={formData.organisation}
              onChange={(e) => set('organisation')(e.target.value)} placeholder="Organisation name"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ent-role" className="text-[#3B3535] font-medium">
              Role <span className="text-[#3B3535]/50">(optional)</span>
            </Label>
            <Input id="ent-role" data-testid="enterprise-role-input" value={formData.role}
              onChange={(e) => set('role')(e.target.value)} placeholder="e.g. Head of Housing, IT Director"
              className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-[#3B3535] font-medium">
              Approximate portfolio / resident scale <span className="text-[#B5541B]">*</span>
            </Label>
            <Select value={formData.scale_band} onValueChange={set('scale_band')}>
              <SelectTrigger data-testid="enterprise-scale-band-select" className="border-gray-200">
                <SelectValue placeholder="Select size band" />
              </SelectTrigger>
              <SelectContent>
                {SCALE_BANDS.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[#3B3535] font-medium">
              Problem / capability of interest <span className="text-[#3B3535]/50">(optional)</span>
            </Label>
            <Select value={formData.capability} onValueChange={set('capability')}>
              <SelectTrigger data-testid="enterprise-capability-select" className="border-gray-200">
                <SelectValue placeholder="Select a capability" />
              </SelectTrigger>
              <SelectContent>
                {CAPABILITIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ent-systems" className="text-[#3B3535] font-medium">
            Existing core systems <span className="text-[#3B3535]/50">(optional)</span>
          </Label>
          <Input id="ent-systems" data-testid="enterprise-systems-input" value={formData.existing_systems}
            onChange={(e) => set('existing_systems')(e.target.value)}
            placeholder="e.g. HMS, PMS, CRM, repairs platform, identity provider"
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]" />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <TriChoice label="Integration needed?" value={formData.integration} onChange={set('integration')} testId="enterprise-integration" />
          <TriChoice label="SSO / identity?" value={formData.sso} onChange={set('sso')} testId="enterprise-sso" />
          <TriChoice label="White-label interest?" value={formData.white_label} onChange={set('white_label')} testId="enterprise-whitelabel" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ent-target" className="text-[#3B3535] font-medium">
            Target team / service / portfolio <span className="text-[#3B3535]/50">(optional)</span>
          </Label>
          <Input id="ent-target" data-testid="enterprise-target-input" value={formData.target_area}
            onChange={(e) => set('target_area')(e.target.value)}
            placeholder="Where would you want to test first?"
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]" />
        </div>

        <div className="space-y-2">
          <Label htmlFor="ent-message" className="text-[#3B3535] font-medium">
            Message <span className="text-[#3B3535]/50">(optional)</span>
          </Label>
          <Textarea id="ent-message" data-testid="enterprise-message-input" value={formData.message}
            onChange={(e) => set('message')(e.target.value)}
            placeholder="What is your existing stack not solving well enough?"
            rows={3}
            className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B] resize-none" />
        </div>

        {error && (
          <div data-testid="enterprise-form-error" className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">{error}</div>
        )}

        <Button
          data-testid="enterprise-submit-btn"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-8 h-auto py-5 rounded-full shadow-lg w-full"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            'Discuss enterprise & integration'
          )}
        </Button>
      </div>
    </div>
  );
};

export default EnterpriseContactForm;
